import { gql, useQuery } from '@apollo/client';
import { GetCalculationByBrandQuery, GetCalculationByBrandQueryVariables, PropertyBrandWithProducts } from '../types/types';
import { useCalculationInput } from 'src/common/context';
import { useState, useEffect } from 'react';
import { BrandEnum } from 'src/common/enums';

/**
 * Get calculation from a specific insurance brand
 *
 * @param input - CalculationInput with property info
 * @param brandCode - 'grawe' or 'wiener'
 * @returns Calculation from the specified brand only
 *
 * @example
 * const { data } = useQuery(getCalculationByBrand, {
 *   variables: {
 *     input: { ... },
 *     brandCode: 'grawe'
 *   }
 * });
 */
export const GET_CALCULATION_BY_BRAND = gql`
  query GetCalculationByBrand($input: CalculationInput!, $brandCode: String!) {
    getCalculationByBrand(input: $input, brandCode: $brandCode) {
      _id
      code
      ptsLogo
      name
      products {
        _id
        name
        code
        actionLabel
        productTypeSpecific {
          coverages {
            code
            label
            description
            disables
          }
          modifiers {
            field
            fixed
            min
            max
            default
          }
        }
        partnerId {
          contractor
          insuredPerson
        }
        calculation {
          calculationId
          sessionId
          packageCode
          packageName
          totalPremium
          installmentsPremium
          singlePremium
          installmentsCalculationId
          singleCalculationId
          buildingQuote
          householdQuote
          policyStartDate
          policyDuration
          coverages {
            code
            name
            description
            premium
            insuredAmount
            risks {
              code
              name
              description
              premium
              insuredAmount
              claims {
                name
                premium
                insuredAmount
              }
            }
          }
          additionalCoverages {
            code
            name
            description
            premium
            insuredAmount
            risks {
              code
              name
              description
              premium
              insuredAmount
              claims {
                name
                premium
                insuredAmount
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Hook to get insurance calculation from a specific brand.
 * Returns API PropertyBrandWithProducts: code, name, products.
 */
export function useGetCalculationByBrand(
  brandCode: BrandEnum,
  options?: { skip?: boolean }
) {
  const { setBrandCode, calculationInput } = useCalculationInput();
  const [isInitialized, setIsInitialized] = useState(false);


  const { data, loading, error, refetch, previousData } = useQuery<GetCalculationByBrandQuery, GetCalculationByBrandQueryVariables>(
    GET_CALCULATION_BY_BRAND,
    {
      variables: { input: calculationInput!, brandCode: brandCode! },
      skip: !calculationInput && !brandCode || options?.skip,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    setIsInitialized(true);
    setBrandCode(brandCode);
  }, []);

  useEffect(() => {
    refetch();
  }, [calculationInput]);

  const isLoading = !isInitialized || loading;
  const calculation: PropertyBrandWithProducts | undefined = calculationInput
    ? (data?.getCalculationByBrand ?? previousData?.getCalculationByBrand)
    : undefined;

  return {
    calculation,
    loading: isLoading,
    error,
    refetch,
  };
}
